import React, { useState } from 'react'
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import {
  useGetDailyStoreStocksQuery,
  useDeleteDailyStoreStockMutation
} from '../../slices/dailyStoreStocksApiSlice'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

const DailyStoreStockListScreen = () => {
  // ─── State ────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState('')
  const [refineFilter, setRefineFilter] = useState('') // second filter
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // ─── Data Fetching ─────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    error,
    refetch
  } = useGetDailyStoreStocksQuery(pageNumber)
  const [deleteDailyStoreStock, { isLoading: loadingDelete }] =
    useDeleteDailyStoreStockMutation()

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this dailyStoreStock?')) return
    try {
      await deleteDailyStoreStock(id).unwrap()
      toast.success('DailyStoreStock deleted!')
      refetch()
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete')
    }
  }

  const paginationHandler = (page) => setPageNumber(page)

  // ─── Filtering Logic (ORIGINAL, unchanged) ────────────────────────────────
  const filterDailyStoreStocks = () => {
    if (!data?.dailyStoreStocks) return []
    return data.dailyStoreStocks.filter((dailyStoreStock) => {
      const dailyStoreStockDate = new Date(dailyStoreStock.date)
      const inRange =
        (!startDate || dailyStoreStockDate >= new Date(startDate)) &&
        (!endDate || dailyStoreStockDate <= new Date(endDate))

      const noteMatch = dailyStoreStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemMatch = dailyStoreStock.dailyStoreStockItems.some((i) =>
        i.name.toLowerCase().includes(filter.toLowerCase())
      )

      return inRange && (noteMatch || itemMatch)
    })
  }

  // ─── Total Value (refine on items with second filter) ─────────────────────
  const calculateTotalDailyStoreStocks = () => {
    return filterDailyStoreStocks().reduce((acc, dailyStoreStock) => {
      const noteMatch = dailyStoreStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirstFilter = noteMatch
        ? dailyStoreStock.dailyStoreStockItems
        : dailyStoreStock.dailyStoreStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecondFilter = itemsAfterFirstFilter.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return (
        acc +
        itemsAfterSecondFilter.reduce(
          (sum, i) => sum + i.qty * i.price,
          0
        )
      )
    }, 0)
  }

  // ─── Excel Export (refine on items with second filter) ────────────────────
  const exportToExcel = () => {
    const rows = filterDailyStoreStocks().flatMap((dailyStoreStock) => {
      const noteMatch = dailyStoreStock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirstFilter = noteMatch
        ? dailyStoreStock.dailyStoreStockItems
        : dailyStoreStock.dailyStoreStockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecondFilter = itemsAfterFirstFilter.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return itemsAfterSecondFilter.map((i) => ({
        Date: new Date(dailyStoreStock.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: dailyStoreStock.note || 'N/A',
        User: dailyStoreStock.username || 'N/A'
      }))
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'DailyStoreStocks')
    XLSX.writeFile(wb, 'DailyStoreStocks_Report.xlsx')
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>DailyStoreStocks</h1>
          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>
          <Link to='/admin/dailystoreStocksearchReplace' className='btn btn-light my-3'>
          Search
          </Link>
          <Link to='/admin/dailystoreStocknotesearchReplace' className='btn btn-light my-3'>
          NoteSearch
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product name"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total DailyStoreStocks: ${calculateTotalDailyStoreStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading dailyStoreStocks'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>USER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterDailyStoreStocks().map((dailyStoreStock) => {
                const noteMatch = dailyStoreStock.note
                  ?.toLowerCase()
                  .includes(filter.toLowerCase())

                const itemsAfterFirstFilter = noteMatch
                  ? dailyStoreStock.dailyStoreStockItems
                  : dailyStoreStock.dailyStoreStockItems.filter((i) =>
                      i.name.toLowerCase().includes(filter.toLowerCase())
                    )

                const itemsAfterSecondFilter = itemsAfterFirstFilter.filter(
                  (i) =>
                    i.name
                      .toLowerCase()
                      .includes(refineFilter.toLowerCase())
                )

                const sorted = [...itemsAfterSecondFilter].sort((a, b) =>
                  a.name.localeCompare(b.name)
                )

                return sorted.map((item) => (
                  <tr key={`${dailyStoreStock._id}-${item.name}`}>
                    <td>
                      {new Date(dailyStoreStock.date).toLocaleDateString()}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>{dailyStoreStock.note || 'N/A'}</td>
                    <td>{dailyStoreStock.username || 'N/A'}</td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="thumb"
                          className="thumbnail-img me-2"
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '5px'
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/dailyStoreStock/${dailyStoreStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        as={Link}
                        to={`/dailyStoreStock/${dailyStoreStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(dailyStoreStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default DailyStoreStockListScreen
